/**
 * Thin fetch wrapper to talk to the emulator backend.
 *
 * All requests go to the same origin (`/api/*`) because:
 *   - dev: Vite proxies /api → http://127.0.0.1:4200
 *   - prod (Day 5): Fastify serves both the static bundle and /api/* on
 *     a single port, so same-origin again.
 *
 * No retry / no auth headers here — the BACKEND owns auth (X-OP-API-Key
 * etc); the frontend just talks to its emulator backend over a trusted
 * local channel.
 */

import type {
  EndpointCatalogResponse,
  HmacValidateResponse,
  ProxyResult,
  ScenarioCatalogResponse,
  WebhookEntry,
} from './types';

async function getJson<T>(path: string): Promise<T> {
  const res = await fetch(path, { method: 'GET' });
  if (!res.ok) {
    throw new Error(`${path}: HTTP ${res.status}`);
  }
  return (await res.json()) as T;
}

async function postJson<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  // The emulator backend returns 200 / 400 / 502 with a structured envelope
  // — we want to surface that body verbatim, not throw on non-2xx.
  if (res.status === 404) {
    throw new Error(`unknown endpoint id (HTTP 404 from emulator)`);
  }
  return (await res.json()) as T;
}

export async function fetchCatalog(): Promise<EndpointCatalogResponse> {
  return getJson<EndpointCatalogResponse>('/api/endpoints');
}

/**
 * `GET /api/party-addresses` — env-derived wire addresses for the
 * default emulator key-pair (`EMULATOR_PRIV_A` / `EMULATOR_PRIV_B`).
 *
 * Required for the Business Demo skin (Issue #20) — the wizard
 * populates `spec_full.parties[]` from these so Gateway tier-3
 * `PUBKEY_NOT_BOUND` check passes.
 */
export async function fetchPartyAddresses(): Promise<{ addrA: string; addrB: string }> {
  return getJson<{ addrA: string; addrB: string }>('/api/party-addresses');
}

export interface ProxyRequest {
  pathParams?: Record<string, string>;
  body?: unknown;
  requestId?: string;
}

export async function callProxy(endpointId: string, req: ProxyRequest): Promise<ProxyResult> {
  return postJson<ProxyResult>(`/api/proxy/${encodeURIComponent(endpointId)}`, req);
}

export interface UploadFileResult {
  /** Content-addressed hash returned by Gateway (e.g. `sha256:abc...`). */
  readonly fileHash: string;
  /** Echoed MIME the file was stored with (server may normalise). */
  readonly mime: string;
  /** Byte length the server actually saw — useful for size receipts. */
  readonly sizeBytes: number;
  /** ISO timestamp returned by Gateway (content-addressed, so it's the original upload time even on re-upload). */
  readonly uploadedAt: string;
  /** True when Gateway sent `idempotent-replayed: true` OR when content was already present (same hash returned). */
  readonly idempotentReplayed?: true;
}

export interface UploadFileError {
  readonly kind:
    | 'too_large'
    | 'unsupported_mime'
    | 'gateway_error'
    | 'gateway_unreachable'
    | 'invalid_response';
  readonly message: string;
  /** HTTP status code from Gateway when available (omitted for unreachable). */
  readonly status?: number;
}

/**
 * Upload a file to Gateway via the emulator's `/api/storage/upload`
 * wrapper (Phase F B-NEW-3). Reads the File as ArrayBuffer in the
 * browser, encodes as base64, ships as JSON, the emulator backend
 * decodes and forwards as multipart to Gateway.
 *
 * Resolves with `{ ok: true, result }` on Gateway 2xx, or
 * `{ ok: false, error }` on a structured failure. Never throws on
 * non-2xx Gateway responses — returns the error in the discriminated
 * union so the wizard can show a friendly message. Throws only on
 * truly exceptional conditions (browser-level fetch failure, runtime
 * out-of-memory while base64-encoding a huge buffer, etc.).
 */
export async function uploadFile(
  file: File,
): Promise<{ ok: true; result: UploadFileResult } | { ok: false; error: UploadFileError }> {
  const buf = await file.arrayBuffer();
  const base64 = encodeArrayBufferBase64(buf);
  const res = await fetch('/api/storage/upload', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      filename: file.name,
      mime: file.type === '' ? 'application/octet-stream' : file.type,
      base64,
    }),
  });
  const parsed = (await res.json()) as Record<string, unknown>;
  if (parsed['status'] === 'invalid_payload') {
    return {
      ok: false,
      error: {
        kind: 'invalid_response',
        message: String(parsed['error_message'] ?? 'invalid payload'),
      },
    };
  }
  if (parsed['proxy_status'] === 'gateway_unreachable') {
    return {
      ok: false,
      error: {
        kind: 'gateway_unreachable',
        message: String(parsed['error_message'] ?? 'gateway unreachable'),
      },
    };
  }
  if (parsed['proxy_status'] === 'ok') {
    const status = Number(parsed['gateway_status_code']);
    const body = parsed['gateway_body'] as Record<string, unknown> | null;
    if (status >= 200 && status < 300 && body !== null) {
      const fileHash = body['file_hash'];
      const mime = body['mime'];
      const sizeBytes = body['size_bytes'];
      const uploadedAt = body['uploaded_at'];
      if (
        typeof fileHash === 'string' &&
        typeof mime === 'string' &&
        typeof sizeBytes === 'number' &&
        typeof uploadedAt === 'string'
      ) {
        const replayed = parsed['idempotent_replayed'] === true;
        return {
          ok: true,
          result: {
            fileHash,
            mime,
            sizeBytes,
            uploadedAt,
            ...(replayed ? ({ idempotentReplayed: true } as const) : {}),
          },
        };
      }
      return {
        ok: false,
        error: {
          kind: 'invalid_response',
          message: `Gateway 2xx body missing canonical fields: ${JSON.stringify(body).slice(0, 200)}`,
        },
      };
    }
    if (status === 413) {
      return {
        ok: false,
        error: {
          kind: 'too_large',
          message: 'Файл слишком большой (Gateway лимит — 50 MiB)',
          status,
        },
      };
    }
    if (status === 415) {
      return {
        ok: false,
        error: {
          kind: 'unsupported_mime',
          message: 'Тип файла не поддерживается (разрешены PDF / PNG / JPEG / JSON и др.)',
          status,
        },
      };
    }
    const errBody = body as { error?: { message?: string } } | null;
    return {
      ok: false,
      error: {
        kind: 'gateway_error',
        message: errBody?.error?.message ?? `Gateway вернул HTTP ${status}`,
        status,
      },
    };
  }
  return {
    ok: false,
    error: {
      kind: 'invalid_response',
      message: 'Unexpected response shape from emulator backend',
    },
  };
}

/**
 * Base64-encode an `ArrayBuffer` in the browser without `Buffer`.
 * The naive `btoa(String.fromCharCode(...new Uint8Array(buf)))` blows
 * the call stack on multi-MiB files; we chunk in 32 KiB windows.
 */
function encodeArrayBufferBase64(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  const CHUNK = 0x8000;
  let binary = '';
  for (let i = 0; i < bytes.length; i += CHUNK) {
    const sub = bytes.subarray(i, Math.min(i + CHUNK, bytes.length));
    binary += String.fromCharCode(...sub);
  }
  return btoa(binary);
}

/**
 * Fetch the JSON snapshot of recent webhook entries. SSE is the
 * preferred way for the live timeline, but the snapshot endpoint is
 * useful for "load more" pagination AND as a non-streaming fallback
 * if the browser SSE pipeline is broken (intermediate proxy, etc.).
 */
export async function fetchWebhookSnapshot(limit?: number): Promise<readonly WebhookEntry[]> {
  const url =
    limit === undefined
      ? '/api/webhooks/snapshot'
      : `/api/webhooks/snapshot?limit=${encodeURIComponent(String(limit))}`;
  const wrap = await getJson<{ count: number; entries: WebhookEntry[] }>(url);
  return wrap.entries;
}

export interface HmacValidateInput {
  rawBody: string;
  secret: string;
  signature?: string;
}

export async function validateWebhookHmac(input: HmacValidateInput): Promise<HmacValidateResponse> {
  return postJson<HmacValidateResponse>('/api/webhooks/hmac-validate', input);
}

/**
 * Scenario catalog (Day 4) — list для UI dropdown'а.
 */
export async function fetchScenarios(): Promise<ScenarioCatalogResponse> {
  return getJson<ScenarioCatalogResponse>('/api/scenarios');
}

/**
 * URL для POST /api/scenarios/:id/run. EventSource ходит ТОЛЬКО GET'ом
 * по умолчанию — для POST-SSE используем `fetch` + ReadableStream reader.
 * См. `pages/ScenarioRunner.tsx` где этот URL потребляется.
 */
export function scenarioRunUrl(scenarioId: string): string {
  return `/api/scenarios/${encodeURIComponent(scenarioId)}/run`;
}
